import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance, API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, Edit, Trash2, Download, Grid3x3, List } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const Products = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryHierarchy, setCategoryHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteFromGHL, setDeleteFromGHL] = useState(true); // Checkbox state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCategoryHierarchy();
  }, [selectedCategory, searchTerm]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axiosInstance.get(`/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCategoryHierarchy = async () => {
    try {
      const response = await axiosInstance.get('/categories/hierarchy');
      setCategoryHierarchy(response.data);
    } catch (error) {
      console.error('Failed to fetch hierarchy:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axiosInstance.delete(`/products/${deleteId}?delete_from_ghl=${deleteFromGHL}`);
      
      // Show success message with GHL status
      let message = 'Product deleted successfully';
      if (response.data.ghl_delete_status?.deleted) {
        message += ' and removed from ExcaliburCRM';
      } else if (response.data.ghl_delete_status?.message) {
        message += ` (${response.data.ghl_delete_status.message})`;
      }
      toast.success(message);
      
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setDeleteId(null);
      setDeleteFromGHL(true); // Reset checkbox for next time
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      window.open(
        `${API}/export/csv${selectedCategory && selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''}`,
        '_blank'
      );
      toast.success('Exporting CSV...');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleSyncFromGHL = async () => {
    try {
      setLoading(true);
      toast.info('Syncing products from Excalibur...');
      
      const response = await axiosInstance.post('/integration/sync-products', {
        direction: 'import' // Only import from GHL
      });
      
      toast.success(`Sync complete! Imported: ${response.data.imported || 0}, Updated: ${response.data.updated_local || 0}`);
      fetchProducts(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from Excalibur');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToGHL = async () => {
    try {
      setLoading(true);
      toast.info('Pushing products to Excalibur...');
      
      const response = await axiosInstance.post('/integration/sync-products', {
        direction: 'export' // Only export to GHL
      });
      
      toast.success(`Push complete! Exported: ${response.data.exported || 0}, Updated: ${response.data.updated_remote || 0}`);
      fetchProducts(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to Excalibur');
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryOptions = (items, depth = 0) => {
    const options = [];
    const indent = '\u00A0\u00A0\u00A0'.repeat(depth); // Non-breaking spaces for indentation
    
    items.forEach((category) => {
      // Add parent category
      const icon = depth === 0 ? '📂' : (category.children && category.children.length > 0 ? '📁' : '└─');
      options.push(
        <option key={category.id} value={category.id}>
          {indent}{icon} {category.name}
        </option>
      );
      
      // Add children recursively
      if (category.children && category.children.length > 0) {
        options.push(...renderCategoryOptions(category.children, depth + 1));
      }
    });
    
    return options;
  };

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  return (
    <div data-testid="products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">Manage your product inventory</p>
        </div>
        <div className="flex gap-3">
          {/* View Toggle */}
          <div className="flex border-2 border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-button"
              className={`px-3 py-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title="Grid View"
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              data-testid="list-view-button"
              className={`px-3 py-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
          
          <Button onClick={handleExport} variant="outline" data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          
          {canEdit && (
            <>
              <Button 
                onClick={handleSyncFromGHL} 
                variant="outline"
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                disabled={loading}
              >
                <Download className="w-4 h-4 mr-2" />
                {loading ? 'Syncing...' : 'Import from Excalibur'}
              </Button>
              
              <Button 
                onClick={handleSyncToGHL} 
                variant="outline"
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-2" />
                {loading ? 'Pushing...' : 'Push to Excalibur'}
              </Button>
            </>
          )}
          
          {canEdit && (
            <Link to="/products/new">
              <Button data-testid="add-product-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search products..."
              data-testid="search-products-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            data-testid="filter-category-select"
            className="px-4 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 min-w-[200px]"
          >
            <option value="all">📁 All Categories</option>
            {renderCategoryOptions(categoryHierarchy, 0)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="card text-center py-12" data-testid="no-products-message">
          <p className="text-slate-500 mb-4">No products found</p>
          {canEdit && (
            <Link to="/products/new">
              <Button>Add Your First Product</Button>
            </Link>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        // Grid View - 6 columns, compact layout
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {products.map((product) => (
            <div key={product.id} className="card-hover" data-testid={`product-card-${product.id}`}>
              {/* Square image placeholder */}
              {(product.images && product.images.length > 0) ? (
                <div className="relative w-full pb-[100%] mb-3 overflow-hidden rounded-lg bg-slate-100">
                  <img
                    src={product.images[product.featured_image_index || 0]}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              ) : product.image_processed_url ? (
                <div className="relative w-full pb-[100%] mb-3 overflow-hidden rounded-lg bg-slate-100">
                  <img
                    src={product.image_processed_url}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-full pb-[100%] relative mb-3 bg-slate-100 rounded-lg flex items-center justify-center">
                  <span className="absolute inset-0 flex items-center justify-center text-slate-400 text-4xl">📦</span>
                </div>
              )}
              
              {/* Product Label */}
              {product.label?.title && (
                <span 
                  className="inline-block px-2 py-1 text-xs font-bold rounded mb-2"
                  style={{ 
                    backgroundColor: product.label.color || '#900008',
                    color: 'white'
                  }}
                >
                  {product.label.title}
                </span>
              )}
              
              <h3 className="text-sm font-semibold text-slate-800 mb-2 line-clamp-2">{product.name}</h3>
              <div className="mb-2">
                {product.variants && product.variants.length > 0 ? (
                  <div className="text-base font-medium text-slate-600">
                    {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}
                  </div>
                ) : (
                  <>
                    <div className="text-base font-bold text-indigo-600">${product.price.toFixed(2)}</div>
                    {product.compare_at_price && (
                      <span className="text-xs text-slate-400 line-through">
                        ${product.compare_at_price.toFixed(2)}
                      </span>
                    )}
                  </>
                )}
              </div>
              
              {product.sku && (
                <div className="text-xs text-slate-500 mb-2">SKU: {product.sku}</div>
              )}
              {canEdit && (
                <div className="flex gap-1 pt-2 border-t border-slate-200">
                  <Link to={`/products/edit/${product.id}`} className="flex-1">
                    <Button variant="outline" className="w-full" size="sm" data-testid={`edit-product-${product.id}`}>
                      <Edit className="w-3 h-3" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(product.id)}
                    data-testid={`delete-product-${product.id}`}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // List View - Compact Table/Spreadsheet View
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Image</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Price / Variants</th>
                  {canEdit && (
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((product) => (
                  <tr 
                    key={product.id} 
                    className="hover:bg-slate-50 transition-colors"
                    data-testid={`product-list-item-${product.id}`}
                  >
                    {/* Small Image */}
                    <td className="px-4 py-2 w-16">
                      {(product.images && product.images.length > 0) ? (
                        <img
                          src={product.images[product.featured_image_index || 0]}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded border border-slate-200"
                        />
                      ) : product.image_processed_url ? (
                        <img
                          src={product.image_processed_url}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
                          <span className="text-slate-400 text-lg">📦</span>
                        </div>
                      )}
                    </td>
                    
                    {/* Name with label */}
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800">{product.name}</span>
                        {product.label?.title && (
                          <span 
                            className="px-1.5 py-0.5 text-xs font-bold rounded"
                            style={{ 
                              backgroundColor: product.label.color || '#900008',
                              color: 'white'
                            }}
                          >
                            {product.label.title}
                          </span>
                        )}
                      </div>
                    </td>
                    
                    {/* SKU */}
                    <td className="px-4 py-2">
                      <span className="text-xs text-slate-600">{product.sku || '-'}</span>
                    </td>
                    
                    {/* Price or Variants */}
                    <td className="px-4 py-2">
                      {product.variants && product.variants.length > 0 ? (
                        <span className="text-sm text-slate-600">
                          {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-indigo-600">${product.price.toFixed(2)}</span>
                          {product.compare_at_price && (
                            <span className="text-xs text-slate-400 line-through">
                              ${product.compare_at_price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    
                    {/* Actions */}
                    {canEdit && (
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Link to={`/products/edit/${product.id}`}>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleteId(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog open={deleteId !== null} onOpenChange={() => {
        setDeleteId(null);
        setDeleteFromGHL(true); // Reset checkbox when dialog closes
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Checkbox for ExcaliburCRM deletion */}
          <div className="flex items-center gap-2 py-3">
            <input
              type="checkbox"
              id="delete-from-ghl"
              checked={deleteFromGHL}
              onChange={(e) => setDeleteFromGHL(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
            />
            <label htmlFor="delete-from-ghl" className="text-sm text-slate-700 cursor-pointer">
              Also delete from ExcaliburCRM
            </label>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Products;
